import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import AddEditEcomCategoryDialog from "../../../components/Dialog/AddEditEcomCategoryDialog";
import CategoryBlockConfirmDialog from "../../../components/Dialog/CategoryBlockConfirmDialog";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllEcomCategory, updateEcomCategoryStatus } from "../../../services/ecomCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "name",
  // "Active Icon",
  "order",
  "description",
  "status",
  "featured",
  "actions",
];

const tableRows = [
  "created_date",
  "ecoomerce_category",
  // "active_Icon",
  "serialNo",
  "ecom_category_description",
  "status",
  "featured",
  "actions",
];

function EcommerceCategory() {
  const { t } = useTranslation(["catalogue", "common"]);
  const dispatch = useDispatch();
  const openDialog = useRef(null);
  const openConfirmDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allCategory, setAllCategory] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.categoryName, 1000);

  useEffect(() => {
    fetchAllEcomCategory(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const fetchAllEcomCategory = async (name) => {
    try {
      setIsLoading(true);
      const response = await getAllEcomCategory({ name });
      if (response.data.status === "success") {
        let data = response?.data.data.reverse();
        setAllCategory(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: fetchAllEcomCategory,
    });
  };

  const handleOpenConfirmDialog = (event, row, index) => {
    openConfirmDialog.current.openDialog({ event, row, index });
  };

  const handleChangeStatus = async (params) => {
    try {
      let payload = {
        status: params.event,
      };
      setIsLoading(true);
      const response = await updateEcomCategoryStatus(params.row._id, payload);
      if (response.data.status === "success") {
        openConfirmDialog.current.closeDialog();
        fetchAllEcomCategory(searchKeys.categoryName);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFeatured = async (event, row) => {
    try {
      let payload = {
        isFeatured: event,
      };
      setIsLoading(true);
      const response = await updateEcomCategoryStatus(row._id, payload);
      if (response.data.status === "success") {
        openConfirmDialog.current.closeDialog();
        fetchAllEcomCategory(searchKeys.categoryName);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExportData = () => {
    return allCategory.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "N/A",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "N/A",
      "Image": item.image || "N/A",
      "Small Icon": item.activeIcon || "N/A",
      "Slug In Romanian": item.mlSlug?.ro || "N/A",
      "Slug In English": item.mlSlug?.en || "N/A",
      "Name In Romanian": item.name?.ro || "N/A",
      "Name In English": item.name?.en || "N/A",
      "Description In Romanian": item.description?.ro || "N/A",
      "Description In English": item.description?.en || "N/A",
      "Sorting Number": item.serialNo || "N/A",
      "Status": item.status ? "Active" : "Inactive",
      "Featured": item.isFeatured ? "Yes" : "No",
      "Total Products At This Category": item.totalProducts || 0,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: fetchAllEcomCategory,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"ecommerce_categories.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.categoryName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      categoryName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.categoryName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                categoryName: e.target.value,
              })
            );
          }}
        />
      </Stack>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <GlobalTable
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allCategory}
        showActions={true}
        showEdit={true}
        handleEdit={handleEdit}
        handleChangeStatus={handleOpenConfirmDialog}
        handleChangeFeatured={handleChangeFeatured}
      />
    );
  }, [allCategory]);

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("ecommerceCategoryList")}
        subtitle={t("ecommerceCategoryDescription")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        {renderTable}
      </PaperContainer>

      <AddEditEcomCategoryDialog ref={openDialog} />

      <CategoryBlockConfirmDialog
        ref={openConfirmDialog}
        handleConfirm={handleChangeStatus}
      />
    </>
  );
}

export default EcommerceCategory;
